import { sortBy } from "lodash";
import {
    ExperienceStatus,
    Merchandise,
    MerchandiseCatalogItem,
    MerchandiseCatalogItemStatus,
    MerchandiseFulfillmentPolicy,
    MerchandiseInventoryPolicy,
    MerchandiseSource,
    OversellPolicy,
    VariantType,
} from "types";

export const MERCHANDISE_API_PATH = "wares";
export const MERCHANDISE_CACHE_KEY = "wares";

export const isMerchandiseVisible = (merchandise: Merchandise) => {
    return merchandise.allowedSources && merchandise.allowedSources.length > 0;
};

export const getSingleVariantCatalogItem = (
    catalogItems?: MerchandiseCatalogItem[],
): MerchandiseCatalogItem | undefined => {
    // TODO XS-1401 : Handle switching from multiple catalog items to single for merchandise with variants
    return catalogItems?.[0];
};

export const getMerchandisePrice = (merchandise: Merchandise): number | null => {
    const catalogItems = merchandise.catalog?.items ?? [];
    if (catalogItems.length > 0) {
        const firstItem = catalogItems[0];
        return firstItem.price ?? firstItem.prices?.perUnit?.price ?? null;
    }
    return null;
};

export const hasMerchandisePricing = (merchandise: Merchandise): boolean => {
    const catalogItems = merchandise.catalog?.items ?? [];
    return catalogItems.some((catalogItem) => (catalogItem.prices?.perUnit?.price ?? catalogItem.price) != null);
};

export const isAutoFulfillment = (merchandise: Merchandise): boolean => {
    return merchandise.warePreference?.fulfillmentPolicy === MerchandiseFulfillmentPolicy.IMMEDIATE;
};

export const hasVariantsEnabled = (merchandise: Merchandise): boolean => {
    return merchandise.warePreference?.variantType === VariantType.MULTIPLE;
};

export const isInventoryTracked = (merchandise: Merchandise): boolean => {
    return merchandise.warePreference?.inventoryPolicy === MerchandiseInventoryPolicy.TRACKED;
};

export const getMerchandiseVariantPrice = (catalogItem: MerchandiseCatalogItem): number => {
    return catalogItem.prices?.perUnit?.price ?? catalogItem.price ?? 0;
};

export const getMerchandiseVariantMaxSelectableQuantity = (
    catalogItem: MerchandiseCatalogItem,
    hasTrackedInventory: boolean,
): number => {
    const oversellPolicy = catalogItem.oversellPolicy ?? OversellPolicy.REQUIRE_AVAILABILITY;

    if (hasTrackedInventory && oversellPolicy !== OversellPolicy.ALLOW_NEGATIVE) {
        return Math.max(catalogItem.available ?? 0, 0);
    }

    return Number.MAX_SAFE_INTEGER;
};

export const isVariantPurchasable = (catalogItem: MerchandiseCatalogItem, hasTrackedInventory: boolean): boolean => {
    return getMerchandiseVariantMaxSelectableQuantity(catalogItem, hasTrackedInventory) > 0;
};

export const isVariantOutOfStock = (catalogItem: MerchandiseCatalogItem, hasTrackedInventory: boolean): boolean => {
    return hasTrackedInventory && (catalogItem.available ?? 0) <= 0;
};

export const getVariantMinQuantity = (variantQuantity: number, totalQuantity: number): number => {
    return totalQuantity === 0 ? 0 : Math.max(variantQuantity - (totalQuantity - 1), 0);
};

export const getVariantMaxUpdateQuantity = (
    catalogItem: MerchandiseCatalogItem | undefined,
    hasTrackedInventory: boolean,
    isHidden: boolean,
    currentQuantity: number,
): number => {
    if (!catalogItem) {
        return currentQuantity;
    }

    const isPurchasable = isVariantPurchasable(catalogItem, hasTrackedInventory);
    const catalogItemMax = getMerchandiseVariantMaxSelectableQuantity(catalogItem, hasTrackedInventory);

    if (isHidden && !isPurchasable) {
        return currentQuantity;
    }

    // If unlimited inventory, return unlimited
    if (catalogItemMax === Number.MAX_SAFE_INTEGER) {
        return Number.MAX_SAFE_INTEGER;
    }

    // Otherwise, allow up to currentQuantity + available stock
    return currentQuantity + catalogItemMax;
};

export const isMerchandiseVariantAvailable = (
    catalogItem: MerchandiseCatalogItem,
    hasTrackedInventory: boolean,
): boolean => {
    return isVariantPublished(catalogItem) && isVariantPurchasable(catalogItem, hasTrackedInventory);
};

export const isVariantPublished = (catalogItem: MerchandiseCatalogItem): boolean => {
    return catalogItem.status === MerchandiseCatalogItemStatus.PUBLISHED && hasCatalogItemPrice(catalogItem);
};

export const getLowestMerchandisePrice = (merchandise: Merchandise): number | null => {
    const prices = getPurchasableMerchandiseCatalogItems(merchandise)
        .map((catalogItem) => catalogItem.prices?.perUnit?.price ?? catalogItem.price)
        .filter((price): price is number => price != null);

    if (prices.length === 0) {
        return null;
    }

    return Math.min(...prices);
};

export const isPurchasableMerchandiseCatalogItem = (catalogItem: MerchandiseCatalogItem): boolean => {
    return catalogItem.status === MerchandiseCatalogItemStatus.PUBLISHED;
};

export const getPurchasableMerchandiseCatalogItems = (merchandise: Merchandise): MerchandiseCatalogItem[] => {
    return (merchandise.catalog?.items ?? []).filter((catalogItem) => isPurchasableMerchandiseCatalogItem(catalogItem));
};

export const filterPurchasableMerchandiseCatalogItems = (merchandise: Merchandise): MerchandiseCatalogItem[] => {
    return getPurchasableMerchandiseCatalogItems(merchandise).filter((catalogItem) => !!catalogItem.template?.id);
};

export const getSortedMerchandiseCatalogItems = (catalogItems: MerchandiseCatalogItem[]): MerchandiseCatalogItem[] => {
    return sortBy(catalogItems, "sequence");
};

export const getSortedPurchasableMerchandiseCatalogItems = (
    catalogItems: MerchandiseCatalogItem[],
): MerchandiseCatalogItem[] => {
    return getSortedMerchandiseCatalogItems(
        catalogItems.filter((catalogItem) => isPurchasableMerchandiseCatalogItem(catalogItem)),
    );
};

export const getPurchasableMerchandiseTemplateIds = (merchandise: Merchandise): Set<ID> => {
    const templateIds = getPurchasableMerchandiseCatalogItems(merchandise)
        .map((catalogItem) => catalogItem.template?.id)
        .filter(Boolean) as ID[];

    return new Set(templateIds);
};

export const getPublicVariantCount = (catalogItems?: MerchandiseCatalogItem[]): number => {
    return (catalogItems ?? []).filter((catalogItem) => catalogItem.status === MerchandiseCatalogItemStatus.PUBLISHED)
        .length;
};

export const hasPublicMerchandiseVariant = (merchandise: Merchandise): boolean => {
    return getPublicVariantCount(merchandise.catalog?.items) > 0;
};

export const getTotalVariantCount = (merchandise: Merchandise): number => {
    return (merchandise.catalog?.items ?? []).length;
};

export const hasCatalogItemPrice = (catalogItem: MerchandiseCatalogItem): boolean => {
    return (catalogItem.prices?.perUnit?.price ?? catalogItem.price) != null;
};

const getPublishedPricedCatalogItems = (catalogItems: MerchandiseCatalogItem[]): MerchandiseCatalogItem[] => {
    return catalogItems.filter((catalogItem) => isVariantPublished(catalogItem));
};

interface MerchandisePurchaseAvailability {
    isUnavailable: boolean;
    isOutOfStock: boolean;
}

export const getMerchandisePurchaseAvailability = (merchandise: Merchandise): MerchandisePurchaseAvailability => {
    const hasTrackedInventory = isInventoryTracked(merchandise);
    const catalogItems = merchandise.catalog?.items ?? [];
    const publishedPricedCatalogItems = getPublishedPricedCatalogItems(catalogItems);

    const isUnavailable = catalogItems.length > 0 && publishedPricedCatalogItems.length === 0;

    if (isUnavailable || !hasTrackedInventory || publishedPricedCatalogItems.length === 0) {
        return {
            isUnavailable,
            isOutOfStock: false,
        };
    }

    const hasPurchasableVariant = publishedPricedCatalogItems.some((catalogItem) =>
        isVariantPurchasable(catalogItem, hasTrackedInventory),
    );

    return {
        isUnavailable: false,
        isOutOfStock: !hasPurchasableVariant,
    };
};

export const isMerchandiseUnavailableForPurchase = (merchandise: Merchandise): boolean => {
    return getMerchandisePurchaseAvailability(merchandise).isUnavailable;
};

export const isMerchandiseOutOfStockForPurchase = (merchandise: Merchandise): boolean => {
    return getMerchandisePurchaseAvailability(merchandise).isOutOfStock;
};

export const getMerchandiseDraftReasons = (merchandise: Merchandise): string[] => {
    const reasons: string[] = [];
    const catalogItems = merchandise.catalog?.items ?? [];
    const hasNoPublicVariants = catalogItems.length > 0 && !hasPublicMerchandiseVariant(merchandise);

    if (hasNoPublicVariants) {
        reasons.push("No Variants are published");
    }

    if (catalogItems.length > 0 && !hasMerchandisePricing(merchandise)) {
        reasons.push("Pricing is not configured");
    }

    if (catalogItems.length === 0) {
        reasons.push("Variants are not configured");
    }

    return reasons;
};

export const isMerchandiseDraft = (merchandise: Merchandise) => {
    return merchandise.status === ExperienceStatus.DRAFT;
};

export const getValidMerchandises = (merchandise: Merchandise[]) => {
    return merchandise.filter((item) => {
        return !isMerchandiseDraft(item) && item.allowedSources?.includes(MerchandiseSource.OFFICE);
    });
};
